/**
 * One conversation, assembled from the two sources that describe it.
 *
 * Story VC-024. `agent_runtime_lld.md` §40.9 (the transcript), §40.9.6 (the
 * seam with the live stream), §28.0 (the turn is on the payload).
 *
 * # Why this is its own file
 *
 * VC-016 kept this inside `useConversation`, where it could not be tested
 * without a browser — and the defect it shipped was an *ordering* one, which
 * is exactly the class a browser is not needed to catch. Nothing here
 * touches the DOM or React, which is why it has a suite.
 *
 * # The seam, in one paragraph
 *
 * A client reading a conversation it has been away from pages the transcript
 * to the end, then opens the event stream. The stream's full resume replays
 * `response_projected` for turns the transcript already carried, and that is
 * not a duplicate to suppress: both sides key an agent turn by `turn_id`
 * (§28.0 put the turn on the payload for this kind of reason), so a replayed
 * revision *replaces* the seeded entry rather than appending beside it. The
 * user's side is never duplicated, because no event carries it.
 */

/**
 * @typedef {{ id: string, speaker: "user" | "agent", text: string,
 *             final: boolean, interrupted?: boolean, spoken?: boolean,
 *             attachments?: number, snapshot?: any }} Turn
 */

/**
 * The turns a transcript page describes. §40.9.4.
 *
 * An agent entry is keyed `agent:<turn_id>` — the same key `upsertAgent`
 * mints — which is the whole of how the replay converges on what was seeded
 * instead of rendering it twice.
 *
 * That key is also why this folds rather than maps. §40.9.2 collapses a
 * turn's partial and final *per page*, so a turn whose entries straddle a
 * page boundary comes back once in each page — the endpoint says so, and
 * says a client keying by turn renders one bubble. This is that client. A
 * plain map would produce two turns with one id, which is a duplicated
 * answer and a React key collision in the same line of code.
 *
 * @param {any[]} entries
 * @returns {Turn[]}
 */
export function seedTurns(entries) {
  const turns = [];
  const at = new Map();
  for (const turn of entries.map((entry) => asTurn(entry))) {
    const index = at.get(turn.id);
    if (index === undefined) {
      at.set(turn.id, turns.length);
      turns.push(turn);
    } else {
      // The later one stands, in the place the earlier one held: §27.3's
      // final supersedes its partial, and moving the bubble to the end
      // would reorder a conversation to report that.
      turns[index] = turn;
    }
  }
  return turns;
}

/**
 * One transcript entry as the turn a renderer takes.
 *
 * @param {any} entry
 * @returns {Turn}
 */
function asTurn(entry) {
  return (
    entry.speaker === "agent"
      ? {
          id: `agent:${entry.turn_id}`,
          speaker: "agent",
          text: prose(entry.snapshot),
          snapshot: entry.snapshot,
          final: true,
        }
      : {
          // Keyed by sequence and not by turn: a turn may hold several of
          // the user's messages (§40.6 joins a message to a running turn),
          // and keying by turn would collapse them into one bubble.
          id: `user:${entry.sequence}`,
          speaker: "user",
          text: words(entry.content),
          attachments: attachmentsIn(entry.content),
          final: true,
        }
  );
}

/**
 * Puts a transcript in front of what this screen already has.
 *
 * Only the entries it does not already hold, and always before them: a
 * message typed while the transcript was still loading belongs after the
 * conversation it was typed into, not before it.
 *
 * @param {Turn[]} current
 * @param {Turn[]} seeded
 */
export function mergeSeed(current, seeded) {
  const held = new Set(current.map((turn) => turn.id));
  return [...seeded.filter((turn) => !held.has(turn.id)), ...current];
}

/**
 * Where a resume frame's snapshot belongs. §40.3.
 *
 * Its *turn*, exactly as a `response_projected` is keyed (§28.0) — which is
 * what makes the replay that follows supersede it rather than render a
 * second bubble beside it. This client used to invent a key here, and a
 * resumed session showed the agent's last answer twice.
 *
 * The response id is the fallback for a response that had no turn, and the
 * constant is the fallback for a frame older than RS-024's wire. Both are
 * still better than a shared key: two unattributable snapshots would
 * otherwise collapse into one bubble.
 *
 * @param {any} snapshot the frame's `snapshot` object
 */
export function resumeKey(snapshot) {
  return snapshot?.turn_id ?? snapshot?.response_id ?? "resumed";
}

/**
 * Replaces the agent turn with this key, or appends it.
 *
 * A snapshot replaces — never merges — which is the runtime's contract
 * unchanged (`agent_runtime_hld.md` §22.4).
 *
 * @param {Turn[]} turns
 * @param {string} key
 * @param {Partial<Turn>} patch
 */
export function upsertAgent(turns, key, patch) {
  const id = `agent:${key}`;
  const index = turns.findIndex((turn) => turn.id === id);
  const next = {
    id,
    speaker: "agent",
    text: "",
    final: false,
    ...(index >= 0 ? turns[index] : {}),
    ...patch,
  };
  if (index < 0) {
    return [...turns, next];
  }
  const copy = turns.slice();
  copy[index] = next;
  return copy;
}

/**
 * A spoken turn, which no event on the text stream carries.
 *
 * The voice socket's transcript state is where these come from, and it
 * updates in place while the words are still arriving — so this replaces the
 * last turn of the same speaker when that turn is not yet final, and appends
 * otherwise.
 *
 * @param {Turn[]} turns
 * @param {"user" | "agent"} speaker
 * @param {Partial<Turn>} view
 */
export function upsertSpoken(turns, speaker, view) {
  if (!view.text) {
    return turns;
  }
  const last = turns[turns.length - 1];
  if (last && last.speaker === speaker && !last.final && last.spoken) {
    const copy = turns.slice();
    copy[copy.length - 1] = { ...last, ...view, spoken: true };
    return copy;
  }
  return [
    ...turns,
    {
      id: `${speaker}:spoken:${turns.length}`,
      speaker,
      spoken: true,
      interrupted: false,
      ...view,
    },
  ];
}

/**
 * Ends whatever was still arriving in this turn.
 *
 * @param {Turn[]} turns
 * @param {string | undefined} turnId
 * @param {string} outcome
 */
export function settle(turns, turnId, outcome) {
  return turns.map((turn) => {
    if (turn.final) {
      return turn;
    }
    if (turnId && turn.id !== `agent:${turnId}`) {
      return turn;
    }
    return {
      ...turn,
      final: true,
      // A cancelled turn is one somebody stopped — a barge-in, or the
      // session closing — and that is what the marker means. A *failed*
      // turn is not interrupted; nobody decided it.
      interrupted: turn.interrupted || outcome === "turn_cancelled",
    };
  });
}

/**
 * `display_string`, when the contract has one.
 *
 * The field name is the *application's* — `agents-example` picked it — and
 * knowing it is this client's business rather than the runtime's, which is
 * why §40.9 hands over the snapshot whole and extracts no prose from it. A
 * contract that spells it differently renders through `AnswerScreen` and
 * simply has no bubble, which is the honest outcome for a client that was
 * not written for it.
 *
 * @param {any} snapshot
 */
export function prose(snapshot) {
  return typeof snapshot?.display_string === "string"
    ? snapshot.display_string
    : "";
}

/**
 * What a user entry says, from the blocks it carries.
 *
 * §40.9.1: a user entry is `ContentBlock`s and not a string, because a
 * message is multimodal. `text` is the kind this renderer has words for, and
 * an `audio` block's transcript is a spoken turn's words — the same thing
 * said a different way.
 *
 * @param {any[] | undefined} content
 */
export function words(content) {
  return (content ?? [])
    .map((block) => {
      if (block?.kind === "text") {
        return block.text ?? "";
      }
      if (block?.kind === "audio") {
        return block.transcript ?? "";
      }
      return "";
    })
    .map((part) => part.trim())
    .filter(Boolean)
    .join("\n");
}

/**
 * How many blocks this screen cannot show.
 *
 * Counted rather than dropped. §40.9.1 returns an attachment as a reference
 * whose bytes no route yet serves (RP-008), so the honest rendering is to
 * say one was there: a turn that reads as empty because the user sent a
 * photograph is the defect VC-024 exists to stop, one layer further on.
 *
 * @param {any[] | undefined} content
 */
export function attachmentsIn(content) {
  return (content ?? []).filter(
    (block) =>
      block?.kind === "image" ||
      block?.kind === "document" ||
      block?.kind === "binary" ||
      (block?.kind === "audio" && !block.transcript),
  ).length;
}
